import * as React from 'react';
import { cn } from '@/lib/utils/classNames';

/**
 * GGC V2. Checkbox — Figma node 166:58 family (Component 02).
 *
 * Native input-backed checkbox with custom 16×16 visual. Supports
 * unchecked / checked / indeterminate states. Borders use
 * `ggc-border-gray-dark` (unchecked) and `ggc-cta-blue` (checked/indet).
 *
 * Setting `indeterminate` does not change `checked`; the native input
 * handles a true dash glyph via ref imperative API.
 */
export interface GgcCheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  /** Render the indeterminate (dash) glyph. */
  indeterminate?: boolean;
  /** Accessible label (also used as visible label when `children` not provided). */
  label?: React.ReactNode;
}

const GgcCheckbox = React.forwardRef<HTMLInputElement, GgcCheckboxProps>(
  ({ className, indeterminate, checked, disabled, label, children, id, ...props }, ref) => {
    const innerRef = React.useRef<HTMLInputElement | null>(null);
    React.useImperativeHandle(ref, () => innerRef.current as HTMLInputElement);

    React.useEffect(() => {
      if (innerRef.current) innerRef.current.indeterminate = Boolean(indeterminate);
    }, [indeterminate]);

    const generatedId = React.useId();
    const inputId = id ?? generatedId;
    const showAsActive = checked || indeterminate;

    return (
      <label
        htmlFor={inputId}
        className={cn(
          'inline-flex items-center gap-[7px] font-ggc text-[12px] text-ggc-text-primary select-none',
          disabled && 'cursor-not-allowed opacity-50',
          !disabled && 'cursor-pointer',
          className
        )}
      >
        <span className="relative inline-flex size-[16px] items-center justify-center">
          <input
            ref={innerRef}
            id={inputId}
            type="checkbox"
            checked={checked}
            disabled={disabled}
            className={cn(
              'peer absolute inset-0 size-full cursor-inherit opacity-0',
              // Disabled inputs swallow hover, which blocks a reason Tooltip
              // wrapped around the checkbox — let hover reach the wrapper
              disabled && 'pointer-events-none'
            )}
            {...props}
          />
          <span
            aria-hidden
            className={cn(
              'pointer-events-none flex size-[16px] items-center justify-center rounded-ggc-sm border-[1.5px] transition-colors',
              showAsActive
                ? 'bg-ggc-cta-blue border-ggc-cta-blue text-white'
                : 'bg-white border-ggc-border-gray-dark'
            )}
          >
            {indeterminate ? (
              <span className="block h-[2px] w-[8px] rounded-full bg-white" />
            ) : checked ? (
              <svg viewBox="0 0 12 12" className="size-[10px]" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M2.5 6.5 5 9l4.5-5.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : null}
          </span>
        </span>
        {(label ?? children) != null && <span>{label ?? children}</span>}
      </label>
    );
  }
);
GgcCheckbox.displayName = 'GgcCheckbox';

export { GgcCheckbox };
